<template>
  <b-row>
    <b-col cols="12">
      <b-row class="mb-1">
        <b-col cols="12">
          <b-card no-body>
            <template #header>
              <div class="d-flex flex-row">
                <h4 class="flex-grow-1">Specifications</h4>
                <b-button
                  variant="link"
                  v-if="!editingInfos"
                  @click="editingInfos = true"
                >
                  <i class="fa fa-pen" />
                </b-button>
              </div>
            </template>
            <b-card-body v-if="editableInfos">
              <EditableInput
                v-for="info of editableInfos"
                :key="info.key"
                :label="info.label"
                :editing="editingInfos"
                :value="info.value"
                @valueChanged="value => onValueChanged(value, info.key)"
              />
            </b-card-body>
            <b-card-footer v-if="editingInfos">
              <b-row>
                <b-col cols="12" class="text-right px-2">
                  <b-button @click="resetInfos" class="mr-2">
                    Cancel
                  </b-button>
                  <b-button @click="saveInfos" variant="primary">
                    Save
                  </b-button>
                </b-col>
              </b-row>
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="mb-1" v-if="canGetAssetsMapping">
        <b-col cols="12">
          <b-card no-body>
            <template #header>
              <div class="d-flex flex-row">
                <h4 class="flex-grow-1">Metadata</h4>
                <b-button
                  variant="link"
                  v-if="!editingMetadata"
                  @click="editingMetadata = true"
                >
                  <i class="fa fa-pen" />
                </b-button>
              </div>
            </template>
            <b-card-body class="asset-metadata-form">
              <Form
                v-if="editingMetadata"
                :formSchema="metadataFormSchema"
                :model="metadata"
              />
              <b-row
                v-else
                class="text-left"
                v-for="field of metadataFields"
                :key="field.key"
              >
                <b-col>
                  <span> {{ field.label }} </span>
                </b-col>
                <b-col>
                  {{ get(field.key) }}
                </b-col>
              </b-row>
            </b-card-body>
            <b-card-footer v-if="editingMetadata">
              <b-row>
                <b-col cols="12" class="text-right px-2">
                  <b-button @click="resetMetadata" class="mr-2">
                    Cancel
                  </b-button>
                  <b-button @click="saveMetadata" variant="primary">
                    Save
                  </b-button>
                </b-col>
              </b-row>
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="mb-1" v-else>
        <b-col cols="12">
          <b-card>
            <h1 class="float-left display-3 mr-4">
              <i class="fa fa-lock"></i>
            </h1>
            <h4 class="pt-3">You are not allowed to see asset metadata.</h4>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="mb-1" v-if="canSearchDevices">
        <b-col cols="12">
          <b-card class="text-left" data-cy="Details-linkedDevices">
            <template #header>
              <div class="d-flex flex-row">
                <h4 class="flex-grow-1">Linked Devices</h4>
                <b-button
                  variant="link"
                  @click="link('link-device-modal')"
                  data-cy="Details-linkDeviceBtn"
                  v-if="canMLinkDevices"
                >
                  <i class="fa fa-link" />
                </b-button>
              </div>
            </template>
            <b-row class="text-left font-weight-bold mb-1">
              <b-col cols="4">
                <span> Device ID </span>
              </b-col>
              <b-col cols="3">
                Reference
              </b-col>
              <b-col cols="4">
                Measure Types
              </b-col>
            </b-row>
            <b-row
              class="text-left"
              v-for="(device, i) of linkedDevices"
              :key="device.id"
            >
              <b-col cols="4">
                <router-link
                  :to="{ name: 'device', params: { deviceId: device.id } }"
                >
                  {{ device.id }}
                </router-link>
              </b-col>
              <b-col cols="3" :data-cy="`Details-device.${i}-reference`">
                {{ device.reference }}
              </b-col>
              <b-col cols="4">
                <template v-if="device.measureTypes.length">
                  <b-badge
                    v-for="measureType of device.measureTypes"
                    class="mr-1"
                    :key="measureType"
                    >{{ measureType }}</b-badge
                  >
                </template>
                <template v-else>
                  N/A
                </template>
              </b-col>
              <b-col cols="1" v-if="canUnlinkDevice">
                <span @click="unlink(device.id)" class="pointer"
                  ><i class="fa fa-unlink"
                /></span>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="mb-1" v-else>
        <b-col cols="12">
          <b-card>
            <h1 class="float-left display-3 mr-4">
              <i class="fa fa-lock"></i>
            </h1>
            <h4 class="pt-3">
              You are not allowed to see asset linked devices.
            </h4>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <b-card class="text-left">
            <template #header>
              <div class="d-flex flex-row">
                <h4 class="flex-grow-1">Last Measures</h4>
                <b-button
                  variant="link"
                  title="Show last measurements in JSON format"
                  v-b-modal.viewLastMeasureAsJSON
                >
                  <i class="fas fa-file-alt"></i>
                </b-button>
              </div>
            </template>
            <template v-if="Object.keys(measures).length">
              <b-row
                class="text-left"
                v-for="[measureType, measurePayload] of measures"
                :key="measureType"
              >
                <b-col cols="12">
                  <span
                    ><b> Last {{ measureType }}: </b></span
                  >
                </b-col>
                <b-col cols="12">
                  {{ getMeasureValue(measureType, measurePayload).label }}
                </b-col>
                <b-col cols="12">
                  {{ Date(measurePayload.updatedAt) }}
                </b-col>
              </b-row>
            </template>
            <template v-else>This asset has no measures yet.</template>
          </b-card>
        </b-col>
      </b-row>
    </b-col>
    <link-device-modal :assetId="assetId" @confirmLinkDevice="onConfirmLink" />
    <b-modal id="viewLastMeasureAsJSON" title="Last measure">
      <pre v-if="Object.keys(measures).length">{{
        JSON.stringify(measures, null, 2)
      }}</pre>
    </b-modal>
  </b-row>
</template>

<script>
import { watch, onMounted } from '@vue/composition-api';
import EditableInput from '@/components/device-manager/EditableInput';
import {
  FormSchemaService,
  MappingFieldsService
} from '@/components/vue-shared-components';
import LinkDeviceModal from './LinkDeviceModal.vue';
import { useDetails } from '@/composition/details';

export default {
  components: {
    EditableInput,
    LinkDeviceModal
  },
  props: {
    payload: {
      type: Object,
      required: true
    },
    metadataMapping: {
      type: Object,
      required: true
    },
    assetId: {
      type: String,
      required: true
    },
    linkedDevices: {
      type: Array,
      default: () => []
    },
    canUpdateAsset: {
      type: Boolean,
      required: true
    },
    canGetAssetsMapping: {
      type: Boolean,
      required: true
    },
    canUnlinkDevice: {
      type: Boolean,
      required: true
    },
    canMLinkDevices: {
      type: Boolean,
      required: true
    },
    canSearchDevices: {
      type: Boolean,
      required: true
    }
  },
  setup(props, ctx) {
    const details = useDetails(props, ctx);

    watch(
      () => props.payload,
      value => {
        details.metadata.value = _.get(value, '_source.metadata', {});
      }
    );

    onMounted(() => {
      const formSchemaService = new FormSchemaService();
      const mappingFieldsService = new MappingFieldsService();

      details.metadata.value = _.get(props.payload, '_source.metadata', {});

      details.metadataFormSchema.value = formSchemaService.generate(
        props.metadataMapping.properties,
        details.metadata.value
      );
      details.metadataFormSchema.value.fields = details.metadataFormSchema.value.fields.map(
        f => {
          f.label = details.formatMetaLabel(f.label);
          return f;
        }
      );
      details.metadataFields.value = mappingFieldsService.getFieldsForTable(
        props.metadataMapping
      );
      details.metadataFields.value = details.metadataFields.value.map(m => {
        m.label = details.formatMetaLabel(m.label);
        return m;
      });
      details.resetEditedInfos();
    });

    return details;
  }
};
</script>

<style lang="scss">
.asset-metadata-form .vue-form-generator {
  .field-input {
    text-align: left;
    width: 100%;
  }

  label {
    width: 137px;
  }

  .field-wrap {
    display: inline-block;
    width: calc(100% - 142px);
  }
}
</style>
